// Toronto-time (America/Toronto) helpers for inventory stats.
//
// Why Toronto and not UTC? VAVA's 3 stores + central kitchen are all in
// Toronto. When Regina opens /统计报表 at 9pm Toronto (= 1am UTC next day)
// and asks for "今天", she means the Toronto calendar day, not UTC's.
//
// Why these helpers vs just toISOString()? Because UTC date defaults
// silently drop late-Toronto events (Toronto May 23 11pm = UTC May 24
// 03:00, which gets a YMD slice of "2026-05-24" — wrong calendar day
// from Regina's perspective). And conversely, on a UTC date picker Regina
// would see "2026-05-24" at 11pm her time, which is confusing.
//
// DST: Toronto is EDT (UTC-4) March-November, EST (UTC-5) Nov-March.
// `Intl.DateTimeFormat` with `timeZone: 'America/Toronto'` handles the
// transition automatically — no hardcoded offset.

const TORONTO_TZ = 'America/Toronto';

// 'en-CA' formats dates as YYYY-MM-DD natively — exactly the wire format
// we use throughout the API + UI for date pickers.
const YMD_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: TORONTO_TZ,
  year: 'numeric', month: '2-digit', day: '2-digit'
});

// 'en-GB' with shortOffset gives us "GMT-04" / "GMT-05" — the simplest way
// to read the current Toronto offset from Intl without parsing weirdness.
const OFFSET_FMT = new Intl.DateTimeFormat('en-GB', {
  timeZone: TORONTO_TZ,
  timeZoneName: 'shortOffset'
});

/** Today's calendar date in Toronto as YYYY-MM-DD. */
export function torontoToday() {
  return YMD_FMT.format(new Date());
}

/** N calendar days ago in Toronto as YYYY-MM-DD.
 *  Computed by subtracting N×86400s from now and re-formatting in Toronto.
 *  This is safe across DST because we never hit the 2am transition by
 *  starting from "now" (which is typically midday) and stepping in 24h
 *  increments — even on a DST-spring day the result lands on a valid
 *  calendar date in Toronto. */
export function torontoNDaysAgo(n) {
  return YMD_FMT.format(new Date(Date.now() - n * 86_400_000));
}

/** Get Toronto's UTC offset (in hours, always negative for Toronto) at
 *  the given instant. EDT = -4, EST = -5. */
function torontoOffsetHoursAt(instant) {
  const parts = OFFSET_FMT.formatToParts(instant);
  const tz = parts.find(p => p.type === 'timeZoneName')?.value || '';
  const m = tz.match(/GMT([+-])(\d+)/);
  if (!m) return -5;        // fallback to EST
  const sign = m[1] === '+' ? 1 : -1;
  return sign * parseInt(m[2], 10);
}

/** UTC ISO 8601 timestamp for the midnight of `ymd` in Toronto.
 *  E.g. torontoMidnightUtcIso('2026-05-23') →
 *       '2026-05-23T04:00:00.000Z' in EDT,
 *       '2026-05-23T05:00:00.000Z' in EST.
 *  Use this when constructing a UTC datetime bound for a Toronto calendar day. */
export function torontoMidnightUtcIso(ymd) {
  // Probe noon-Toronto of that date to get the offset (avoids DST 2am edge).
  const probe = new Date(ymd + 'T12:00:00Z');
  const offsetHours = torontoOffsetHoursAt(probe);
  // Toronto midnight in UTC = absolute offset hours after UTC midnight.
  const [y, m, d] = ymd.split('-').map(Number);
  const utcMidnight = Date.UTC(y, m - 1, d) + Math.abs(offsetHours) * 3_600_000;
  return new Date(utcMidnight).toISOString();
}

/** UTC ISO 8601 timestamp for the end-exclusive of `ymd` in Toronto
 *  (= midnight of the next Toronto calendar day). Use as the upper SQL
 *  bound in a "captures all of `ymd` in Toronto" half-open window. */
export function torontoEndExclusiveUtcIso(ymd) {
  // Increment the calendar date by 1 using Date's local-time arithmetic
  // and getter pair (which sidesteps timezones — both the constructor and
  // the getters use the same local-time semantics, so the round-trip
  // returns the intended calendar date regardless of runtime TZ).
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d + 1);
  const nextYmd =
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  return torontoMidnightUtcIso(nextYmd);
}
